import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { AlertCircle, ChevronDown, RefreshCw } from "lucide-react";

import { CompanyContextPanel } from "@/components/company-context-panel";
import { ModelRoutingSettingsPanel } from "@/components/model-routing-settings-panel";
import { PageScaffold } from "@/components/page-scaffold";
import { TenantAgentsPanel } from "@/components/tenant-agents-panel";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAdmin } from "@/hooks/use-admin";
import { useModelRouting } from "@/hooks/use-model-routing";
import {
  agentConfigurationPath,
  agentConfigurationTabFromPath,
  isAgentConfigurationTab,
  resolveAgentConfigurationTab,
} from "@/lib/admin-route-tabs";
import { cn } from "@/lib/utils";

interface AdminAgentConfigurationPanelProps {
  getToken: () => Promise<string>;
  onBack: () => void;
}

/**
 * Platform-admin agent configuration stays deliberately narrower than tenant
 * administration: it reuses the existing tenant-list endpoint solely to pick
 * the tenant whose shared agents should be edited.
 */
export function AdminAgentConfigurationPanel({
  getToken,
  onBack,
}: AdminAgentConfigurationPanelProps) {
  const { t } = useTranslation();
  const [pathname, navigate] = useLocation();
  const { tenants, loading, error, refresh } = useAdmin(getToken);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const activeTab = resolveAgentConfigurationTab(agentConfigurationTabFromPath(pathname));
  const modelRouting = useModelRouting(getToken, {
    tenantId: selectedTenantId,
    enabled: Boolean(selectedTenantId),
  });
  const selectedTenant = tenants.find((tenant) => tenant.id === selectedTenantId);
  const instanceLabel = selectedTenant?.name
    ?? selectedTenantId
    ?? (loading ? t("agentConfiguration.loadingInstances") : t("agentConfiguration.selectInstance"));

  // A refreshed tenant list can remove the selected tenant. Do not retain an
  // editor targeting a tenant that is no longer available to this admin.
  useEffect(() => {
    if (selectedTenantId && !tenants.some((tenant) => tenant.id === selectedTenantId)) {
      setSelectedTenantId(null);
    }
  }, [selectedTenantId, tenants]);

  const selectTab = useCallback((tab: string) => {
    if (isAgentConfigurationTab(tab)) {
      navigate(agentConfigurationPath(tab));
    }
  }, [navigate]);

  return (
    <Tabs
      value={activeTab}
      onValueChange={selectTab}
      className="h-full"
      data-testid="admin-agent-configuration-panel"
    >
      <PageScaffold
        title={<h1 className="truncate text-sm font-semibold tracking-tight">{t("header.agentConfiguration")}</h1>}
        backAriaLabel={t("common:actions.backToChat")}
        onBack={onBack}
        contentClassName="space-y-3"
        headerActions={(
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 min-w-0 max-w-full gap-1.5 px-2.5 text-xs font-medium"
                aria-label={t("agentConfiguration.selectInstance")}
                aria-busy={loading || undefined}
                disabled={loading}
              >
                {loading ? <Spinner className="size-3.5" /> : null}
                <span className="min-w-0 max-w-[12rem] truncate">{instanceLabel}</span>
                <ChevronDown className="size-3 shrink-0 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[14rem] max-w-[calc(100vw-2rem)]">
              {tenants.length ? (
                tenants.map((tenant) => (
                  <DropdownMenuItem
                    key={tenant.id}
                    className={cn(
                      "text-xs",
                      tenant.id === selectedTenantId && "bg-accent font-medium",
                    )}
                    onSelect={() => setSelectedTenantId(tenant.id)}
                  >
                    {tenant.name} ({tenant.id})
                  </DropdownMenuItem>
                ))
              ) : (
                <div className="px-2 py-3 text-center text-xs text-muted-foreground">
                  {t("agentConfiguration.noInstances")}
                </div>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="gap-2 text-xs"
                onSelect={() => void refresh()}
                disabled={loading}
              >
                <RefreshCw className="size-3.5" />
                {t("agentConfiguration.refreshInstances")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        toolbar={(
          <TabsList aria-label={t("agentConfiguration.sections")} className="h-10 min-w-max">
            <TabsTrigger value="company-context">{t("agentConfiguration.companyContext")}</TabsTrigger>
            <TabsTrigger value="model-configuration">{t("agentConfiguration.modelConfiguration")}</TabsTrigger>
            <TabsTrigger value="shared-agents">{t("agentConfiguration.sharedAgents")}</TabsTrigger>
          </TabsList>
        )}
      >

        {error ? (
          <div
            className="flex items-start gap-2 rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            role="alert"
          >
            <AlertCircle className="mt-0.5 size-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}

        <TabsContent value="company-context" className="mt-0">
          <CompanyContextPanel getToken={getToken} />
        </TabsContent>

        <TabsContent value="model-configuration" className="mt-0">
          {selectedTenantId ? (
            <ModelRoutingSettingsPanel modelRouting={modelRouting} />
          ) : (
            <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
              {t("agentConfiguration.selectInstanceForRouting")}
            </div>
          )}
        </TabsContent>

        <TabsContent value="shared-agents" className="mt-0">
          {selectedTenantId ? (
            <TenantAgentsPanel
              getToken={getToken}
              tenantId={selectedTenantId}
              modelRouting={modelRouting}
            />
          ) : (
            <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
              {t("agentConfiguration.selectInstanceForAgents")}
            </div>
          )}
        </TabsContent>
      </PageScaffold>
    </Tabs>
  );
}
